import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ATTENDANCE_STATUS_LABEL,
  FLAG_ICON,
  FLAG_LABEL,
  FLAG_TYPES,
  ROLE_LABEL,
  type FlagType,
} from '@doit/shared';
import { MessageSquare, MessageSquarePlus, Phone, TriangleAlert, X } from 'lucide-react';
import clsx from 'clsx';
import { Modal } from '../../components/Modal';
import { Button, EmptyState, PageHeader, Spinner } from '../../components/ui';
import { Badge } from '../../components/badges';
import { Textarea } from '../../components/form';
import { FiltersBar, useDashboardFilters } from '../../components/DashboardFilters';
import { useToast } from '../../components/Toast';
import { ApiError } from '../../api/client';
import {
  useBoard,
  useCreateNote,
  useRemoveFlag,
  useSetFlag,
  useSettings,
  useStudentNotes,
} from '../../api/queries';
import type { BoardCard, StudentNote, TimelinePoint } from '../../api/types';
import { formatDate, formatDateTime, formatPercent } from '../../lib/format';
import { zoneColor } from '../../lib/zone';
import { AttendanceTimeline } from '../students/AttendanceTimeline';

type Column = 'RISK' | FlagType;

const COLUMNS: { id: Column; icon: string; label: string; accent: string }[] = [
  { id: 'RISK', icon: '🔴', label: 'დასამუშავებელი', accent: 'border-t-danger' },
  { id: 'CANNOT_REACH', icon: FLAG_ICON.CANNOT_REACH, label: FLAG_LABEL.CANNOT_REACH, accent: 'border-t-warn' },
  { id: 'EMAIL_SENT', icon: FLAG_ICON.EMAIL_SENT, label: FLAG_LABEL.EMAIL_SENT, accent: 'border-t-brand-500' },
  { id: 'MONITORING', icon: FLAG_ICON.MONITORING, label: FLAG_LABEL.MONITORING, accent: 'border-t-ink-mute' },
  { id: 'DROPPED_OUT', icon: FLAG_ICON.DROPPED_OUT, label: FLAG_LABEL.DROPPED_OUT, accent: 'border-t-ink' },
];

export function BoardPage() {
  const { filters } = useDashboardFilters();
  const { data, isLoading } = useBoard(filters);
  const { data: settings } = useSettings();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const total = data
    ? Object.values(data.board).reduce((sum, cards) => sum + cards.length, 0)
    : 0;

  // ბარათი ყოველთვის ახალი მონაცემიდან იკითხება — სტატუსის შეცვლის ან
  // კომენტარის დამატების შემდეგ მოდალი ძველ სურათს აღარ აჩვენებს
  const selectedCard =
    (selectedId &&
      Object.values(data?.board ?? {})
        .flat()
        .find((c) => c.enrollmentId === selectedId)) ||
    null;

  return (
    <>
      <PageHeader
        title="გაცდენები"
        subtitle={
          data
            ? `${total} ბარათი · რისკი: დასწრება < ${data.riskThreshold}% ან ${data.streakRiskMin} ზედიზედ გაცდენა`
            : undefined
        }
      />

      <FiltersBar />

      {isLoading || !data ? (
        <div className="flex justify-center py-16">
          <Spinner className="size-6" />
        </div>
      ) : total === 0 ? (
        <div className="rounded-[10px] border border-line bg-surface shadow-xs">
          <EmptyState
            title="რისკში სტუდენტები არ არიან"
            description="ამ ფილტრებით ყველა სტუდენტს ზღვარზე მაღალი დასწრება აქვს."
          />
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          {COLUMNS.map((col) => {
            const cards = data.board[col.id] ?? [];
            return (
              <section
                key={col.id}
                className={clsx(
                  'flex flex-col rounded-[10px] border border-t-2 border-line bg-surface shadow-xs',
                  col.accent,
                )}
              >
                <header className="flex items-center gap-2 border-b border-line px-3 py-2.5">
                  <span className="text-sm">{col.icon}</span>
                  <span className="min-w-0 flex-1 truncate text-[12px] font-semibold text-ink">
                    {col.label}
                  </span>
                  <span className="tabular rounded bg-canvas px-1.5 py-0.5 text-[11px] font-medium text-ink-soft">
                    {cards.length}
                  </span>
                </header>

                <div className="max-h-[calc(100vh-320px)] min-h-24 space-y-2 overflow-y-auto p-2">
                  {cards.length === 0 ? (
                    <p className="px-2 py-6 text-center text-[11px] text-ink-mute">ცარიელია</p>
                  ) : (
                    cards.map((card) => (
                      <Card
                        key={card.enrollmentId}
                        card={card}
                        settings={settings}
                        onClick={() => setSelectedId(card.enrollmentId)}
                      />
                    ))
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}

      <CardModal
        card={selectedCard}
        onClose={() => setSelectedId(null)}
        streakRiskMin={data?.streakRiskMin ?? 3}
      />
    </>
  );
}

// ============================================================

function Card({
  card,
  settings,
  onClick,
}: {
  card: BoardCard;
  settings: { risk_threshold: number; yellow_threshold: number; red_threshold: number } | undefined;
  onClick: () => void;
}) {
  const toast = useToast();

  async function copyPhone() {
    if (!card.phone) return;
    try {
      await navigator.clipboard.writeText(card.phone);
      toast.success('ნომერი დაკოპირდა');
    } catch {
      toast.error('კოპირება ვერ მოხერხდა — მონიშნე ხელით');
    }
  }

  return (
    <div className="rounded-lg border border-line bg-surface transition hover:border-brand-300 hover:shadow-sm">
      <button onClick={onClick} className="w-full p-2.5 pb-2 text-left">
        <div className="mb-1 flex items-start justify-between gap-2">
          <span className="min-w-0 flex-1 truncate text-[12px] font-medium text-ink">
            {card.fullName}
          </span>
          <span
            className="tabular shrink-0 rounded px-1.5 py-0.5 text-[11px] font-semibold text-white"
            style={{ background: zoneColor(card.pct, settings) }}
          >
            {formatPercent(card.pct, 0, '—')}
          </span>
        </div>

        <p className="tabular truncate text-[11px] text-ink-mute">{card.groupName}</p>

        {/* დასწრების ზოლი — იგივე რიგი, რაც სტუდენტის ქარდზე: მარცხნიდან მარჯვნივ */}
        <MiniTimeline timeline={card.timeline} absent={card.absent} countable={card.countable} />

        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          {card.callDueAt && (
            <span className="inline-flex items-center gap-0.5 rounded bg-brand-50 px-1.5 py-0.5 text-[10px] font-medium text-brand-700">
              <Phone className="size-2.5" />
              დასარეკი
            </span>
          )}
          {card.streak > 0 && (
            <span
              className={clsx(
                'inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-medium',
                card.streak >= 3 ? 'bg-danger-soft text-danger' : 'bg-canvas text-ink-mute',
              )}
            >
              <TriangleAlert className="size-2.5" />
              {card.streak} ზედიზედ
            </span>
          )}
          {card.noteCount > 0 && (
            <span className="inline-flex items-center gap-0.5 rounded bg-canvas px-1.5 py-0.5 text-[10px] text-ink-mute">
              <MessageSquare className="size-2.5" />
              {card.noteCount}
            </span>
          )}
          {card.riskReason === 'streak' && (
            <span className="rounded bg-warn-soft px-1.5 py-0.5 text-[10px] text-warn">
              მხოლოდ streak
            </span>
          )}
        </div>
      </button>

      {/* ნომერი ბარათზევე — დაკლიკებით კოპირდება */}
      {card.phone && (
        <button
          type="button"
          onClick={copyPhone}
          title="დააკლიკე ნომრის დასაკოპირებლად"
          className="tabular flex w-full items-center gap-1 border-t border-line px-2.5 py-1.5 text-left text-[11px] text-ink-soft transition hover:bg-canvas hover:text-brand-700"
        >
          <Phone className="size-2.5 shrink-0" />
          {card.phone}
        </button>
      )}
    </div>
  );
}

/** კომპაქტური დასწრების ზოლი ბარათისთვის — ქრონოლოგიურად, მარცხნიდან მარჯვნივ */
function MiniTimeline({
  timeline,
  absent,
  countable,
}: {
  timeline: TimelinePoint[];
  absent: number;
  countable: number;
}) {
  if (timeline.length === 0) return null;

  return (
    <div className="mt-1.5">
      <div className="flex flex-wrap gap-[2px]">
        {timeline.map((p) => (
          <span
            key={p.sessionId}
            title={`#${p.orderNo} · ${formatDate(p.date)} · ${ATTENDANCE_STATUS_LABEL[p.status]}${
              p.topic ? ` · ${p.topic}` : ''
            }`}
            className={clsx(
              'h-3.5 w-[5px] rounded-[1px]',
              p.status === 'PRESENT' ? 'bg-ok' : 'bg-danger',
            )}
          />
        ))}
      </div>
      <p className="tabular mt-1 text-[10px] text-ink-mute">
        გაცდენა {absent} / {countable}
      </p>
    </div>
  );
}

// ============================================================

function CardModal({
  card,
  onClose,
  streakRiskMin,
}: {
  card: BoardCard | null;
  onClose: () => void;
  streakRiskMin: number;
}) {
  const toast = useToast();
  const setFlag = useSetFlag();
  const removeFlag = useRemoveFlag();
  const [note, setNote] = useState('');

  // კომენტარების ნაკადი სტუდენტის ქარდის იგივეა — ერთი ისტორია ორივე ეკრანზე
  const { data: notes, isLoading: notesLoading } = useStudentNotes(card?.studentId);
  const createNote = useCreateNote(card?.studentId ?? '');

  // სხვა ბარათზე გადასვლისას ნაწერი ტექსტი არ უნდა გადმოყვეს
  useEffect(() => setNote(''), [card?.enrollmentId]);

  async function apply(flagType: FlagType) {
    if (!card) return;
    try {
      await setFlag.mutateAsync({ enrollmentId: card.enrollmentId, flagType, note: note || undefined });
      toast.success(
        note.trim() ? `სტატუსი: ${FLAG_LABEL[flagType]} · კომენტარი შენახულია` : `სტატუსი: ${FLAG_LABEL[flagType]}`,
      );
      setNote('');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'ვერ შეიცვალა');
    }
  }

  /** კომენტარი სტატუსის შეცვლის გარეშე — იმავე ისტორიაში ჯდება */
  async function addNote() {
    if (!card) return;
    if (!note.trim()) {
      toast.error('კომენტარი ცარიელია');
      return;
    }
    try {
      await createNote.mutateAsync({
        studentId: card.studentId,
        groupId: card.groupId,
        text: note,
        isPrivate: false,
        nextCallAt: null,
      });
      setNote('');
      toast.success('კომენტარი დაემატა');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'ვერ დაემატა');
    }
  }

  async function clear() {
    if (!card) return;
    try {
      await removeFlag.mutateAsync(card.enrollmentId);
      toast.success('სტატუსი მოიხსნა — ბარათი რისკში დაბრუნდა');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'ვერ მოიხსნა');
    }
  }

  return (
    <Modal
      open={Boolean(card)}
      onClose={onClose}
      size="lg"
      title={card?.fullName ?? ''}
      description={card ? `${card.groupName} · ${card.courseName}` : undefined}
    >
      {card && (
        <div className="space-y-5">
          {/* --- მაჩვენებლები --- */}
          <div className="grid grid-cols-3 gap-2 text-center">
            <Metric label="დასწრება" value={formatPercent(card.pct, 1, '—')} />
            <Metric
              label="ზედიზედ გაცდენა"
              value={String(card.streak)}
              danger={card.streak >= streakRiskMin}
            />
            <Metric label="გაცდენა / სულ" value={`${card.absent} / ${card.countable}`} />
          </div>

          {/* --- დასწრების ისტორია --- */}
          <AttendanceTimeline timeline={card.timeline} />

          {/* --- კონტაქტი --- */}
          <div className="space-y-1.5 rounded-lg bg-canvas px-3.5 py-3 text-[13px]">
            {card.phone && (
              <p className="flex items-center gap-2">
                <Phone className="size-3.5 text-ink-mute" />
                <a href={`tel:${card.phone}`} className="tabular text-ink hover:underline">
                  {card.phone}
                </a>
              </p>
            )}
            {card.email && (
              <p className="truncate text-ink-soft">
                <a href={`mailto:${card.email}`} className="hover:underline">
                  {card.email}
                </a>
              </p>
            )}
            <p className="text-[12px] text-ink-mute">
              ლექტორი: {card.lecturers.map((l) => l.fullName).join(', ') || '—'}
            </p>
          </div>

          {card.flag && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg bg-brand-50 px-3.5 py-3">
              <p className="text-[12px] font-medium text-brand-800">
                {FLAG_ICON[card.flag.flagType]} {FLAG_LABEL[card.flag.flagType]}
              </p>
              <span className="ml-auto text-[11px] text-brand-700">
                {formatDateTime(card.flag.updatedAt)}
              </span>
            </div>
          )}

          {/* --- კომენტარი + სტატუსი --- */}
          <div className="space-y-3">
            <p className="text-[13px] font-medium text-ink-soft">კომენტარი და სტატუსი</p>
            <Textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="კომენტარი — მაგ. დავურეკე, არ უპასუხა. ტექსტი ისტორიაში შეინახება"
            />
            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                variant="secondary"
                icon={<MessageSquarePlus className="size-4" />}
                onClick={() => void addNote()}
                loading={createNote.isPending}
              >
                მხოლოდ კომენტარი
              </Button>
              <span className="text-[11px] text-ink-mute">
                ან აირჩიე სტატუსი — კომენტარი მასთან ერთად შეინახება
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {FLAG_TYPES.map((t) => (
                <Button
                  key={t}
                  size="sm"
                  variant={card.flag?.flagType === t ? 'primary' : 'secondary'}
                  onClick={() => void apply(t)}
                  loading={setFlag.isPending}
                >
                  {FLAG_ICON[t]} {FLAG_LABEL[t]}
                </Button>
              ))}
              {card.flag && (
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<X className="size-4" />}
                  onClick={() => void clear()}
                  loading={removeFlag.isPending}
                >
                  მოხსნა
                </Button>
              )}
            </div>
          </div>

          {/* --- კომენტარების ისტორია --- */}
          <NoteHistory notes={notes} loading={notesLoading} />

          <Link
            to={`/students/${card.studentId}`}
            className="block rounded-lg border border-line px-3.5 py-2.5 text-center text-[13px] font-medium text-brand-700 transition hover:bg-canvas"
          >
            სტუდენტის ქარდის გახსნა →
          </Link>
        </div>
      )}
    </Modal>
  );
}

/**
 * კომენტარების ისტორია — იგივე ნაკადი, რაც სტუდენტის ქარდზე:
 * უახლესი ზემოთ, თითოეულს თარიღი, ავტორი და (თუ სტატუსთან ერთად დაიწერა) სტატუსი.
 */
function NoteHistory({ notes, loading }: { notes: StudentNote[] | undefined; loading: boolean }) {
  return (
    <div className="space-y-2">
      <p className="text-[13px] font-medium text-ink-soft">
        კომენტარების ისტორია{notes ? ` (${notes.length})` : ''}
      </p>

      {loading ? (
        <div className="flex justify-center py-4">
          <Spinner />
        </div>
      ) : (notes ?? []).length === 0 ? (
        <p className="rounded-lg bg-canvas px-3.5 py-3 text-[12px] text-ink-mute">
          კომენტარი ჯერ არ დაწერილა.
        </p>
      ) : (
        <ul className="max-h-64 divide-y divide-line overflow-y-auto rounded-lg border border-line px-3">
          {(notes ?? []).map((n) => (
            <li key={n.id} className="py-2.5">
              <div className="mb-1 flex flex-wrap items-center gap-1.5 text-[11px]">
                <span className="font-medium text-ink">{n.author?.fullName ?? 'წაშლილი'}</span>
                {n.author && <Badge>{ROLE_LABEL[n.author.role]}</Badge>}
                {n.flagType && (
                  <Badge tone="brand">
                    {FLAG_ICON[n.flagType]} {FLAG_LABEL[n.flagType]}
                  </Badge>
                )}
                {n.isPrivate && <Badge tone="warn">პირადი</Badge>}
                {n.nextCallAt && (
                  <Badge tone="ok">
                    <Phone className="mr-0.5 inline size-2.5" />
                    დარეკვა {formatDate(n.nextCallAt)}
                  </Badge>
                )}
                <span className="ml-auto text-ink-mute">{formatDateTime(n.createdAt)}</span>
              </div>
              <p className="whitespace-pre-wrap text-[12px] leading-relaxed text-ink-soft">
                {n.text}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Metric({ label, value, danger }: { label: string; value: string; danger?: boolean }) {
  return (
    <div className="rounded-lg bg-canvas px-2 py-2.5">
      <p className={clsx('tabular text-lg font-semibold', danger ? 'text-danger' : 'text-ink')}>
        {value}
      </p>
      <p className="text-[10px] leading-tight text-ink-mute">{label}</p>
    </div>
  );
}
